import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { TourButton } from '../tour';
import { 
  LayoutDashboard, 
  Shield, 
  FileText, 
  AlertTriangle, 
  FileCheck, 
  Folder,
  Plug,
  FileSearch,
  LogOut,
  Menu,
  X,
  Network,
  Layers,
  Upload,
  TrendingUp,
  MessageSquare,
  Hash,
  Key,
  TrendingDown,
  Clock,
  Settings,
  Database,
  Bug,
  Scan,
  Activity,
  ShieldCheck,
  Globe,
  CreditCard,
  HeartPulse,
  BarChart3
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../ui/button';

const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/ctem', icon: Layers, label: 'CTEM' },
  { path: '/vulnerabilities', icon: Bug, label: 'Vulnerabilities' },
  { path: '/vulnerability-scanner', icon: Scan, label: 'Scanner' },
  { path: '/continuous-monitoring', icon: Activity, label: 'Monitoring' },
  { path: '/soc2', icon: ShieldCheck, label: 'SOC 2 Readiness' },
  { path: '/threat-intel', icon: Globe, label: 'Threat Intel' },
  { path: '/controls', icon: Shield, label: 'Controls' },
  { path: '/control-mapping', icon: Network, label: 'Control Mapping' },
  { path: '/evidence', icon: FileCheck, label: 'Evidence Lab' },
  { path: '/bulk-import', icon: Upload, label: 'Bulk Import' },
  { path: '/risks', icon: AlertTriangle, label: 'Risk Register' },
  { path: '/policies', icon: FileText, label: 'Policies' },
  { path: '/frameworks', icon: Folder, label: 'Frameworks' },
  { path: '/custom-frameworks', icon: Layers, label: 'Custom Frameworks' },
  { path: '/compliance-metrics', icon: TrendingUp, label: 'Metrics' },
  { path: '/compliance-drift', icon: TrendingDown, label: 'Drift Detection' },
  { path: '/advanced-reporting', icon: BarChart3, label: 'Reports & Analytics' },
  { path: '/scheduler', icon: Clock, label: 'Scheduler' },
  { path: '/integrations', icon: Plug, label: 'Integrations' },
  { path: '/integration-health', icon: HeartPulse, label: 'Integration Health' },
  { path: '/connectors', icon: Database, label: 'Connectors' },
  { path: '/teams-integration', icon: MessageSquare, label: 'Teams' },
  { path: '/slack-integration', icon: Hash, label: 'Slack' },
  { path: '/saml-sso', icon: Key, label: 'SAML SSO' },
  { path: '/billing', icon: CreditCard, label: 'Billing' },
  { path: '/audit-portal', icon: FileSearch, label: 'Audit Portal' },
  { path: '/demo', icon: Database, label: 'Demo Mode' },
];

const adminNavItems = [
  { path: '/admin', icon: Settings, label: 'Admin Panel', adminOnly: true },
];

export const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-layout">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-slate-900 text-white rounded-sm"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Sidebar */}
      <aside 
        className={`sidebar transition-transform duration-300 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-slate-800" data-tour="sidebar-logo">
          <h1 className="font-secondary font-bold text-xl tracking-tight">
            GRC Platform
          </h1>
          <p className="text-slate-500 text-xs mt-1 font-mono">ZENTRY</p>
        </div>

        <nav className="py-4 flex-1">
          {navItems.map((item) => {
            const tourId = item.label.toLowerCase().replace(/\s+/g, '-');
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${tourId}`}
                data-tour={`nav-${tourId}`}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          {/* Admin-only navigation items */}
          {user?.role === 'admin' && adminNavItems.map((item) => {
            const tourId = item.label.toLowerCase().replace(/\s+/g, '-');
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${tourId}`}
                data-tour={`nav-${tourId}`}
                className={({ isActive }) =>
                  `sidebar-nav-item ${isActive ? 'active' : ''}`
                }
                onClick={() => setMobileMenuOpen(false)}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-medium">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Top Bar */}
        <header className="page-header">
          <div className="page-header-content">
            <div>
              <p className="text-sm text-slate-500">Welcome back,</p>
              <p className="text-base font-semibold text-slate-900">{user?.name || 'User'}</p>
            </div>
            <div className="flex items-center gap-4">
              <TourButton />
            </div>
          </div>
        </header>
        <div className="page-content" data-tour="page-content">
          {children}
        </div>
      </main>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
